import { RSAA } from 'redux-api-middleware';
import constants from '../constants';
import { ACTIVATE_USER_SUCCESS } from './authentication';
import * as entityHandlers from '../modules/bin/entityHandlers';
import { Record } from './bin/utility';
import get from 'lodash.get';
import { localStorageKeys } from '../constants/localStorageKeys';
import { GET_ORGANIZATION_SUCCESS } from './general';
import { writeOrgInfo } from '../helpers/orgInfoCache';
import {
  giftAidStatusPath,
  readGiftAidStatus
} from '../pages/organization/swiftaidConnection/constants';

export const MFA_REQUIRED = 'session/MFA_REQUIRED';
export const VERIFY_MFA_REQUESTED = 'session/VERIFY_MFA_REQUESTED';
export const VERIFY_MFA_SUCCESS = 'session/VERIFY_MFA_SUCCESS';
export const VERIFY_MFA_FAILURE = 'session/VERIFY_MFA_FAILURE';
export const MFA_CLEAR = 'session/MFA_CLEAR';

export const AUTHENTICATE_REQUESTED = 'session/AUTHENTICATE_REQUESTED';
export const AUTHENTICATE_SUCCESS = 'session/AUTHENTICATE_SUCCESS';
export const AUTHENTICATE_FAILURE = 'session/AUTHENTICATE_FAILURE';

export const RESUME_SESSION_NOT_POSSIBLE =
  'session/RESUME_SESSION_NOT_POSSIBLE';
export const RESUME_SESSION_REQUESTED = 'session/RESUME_SESSION_REQUESTED';
export const RESUME_SESSION_SUCCESS = 'session/RESUME_SESSION_SUCCESS';
export const RESUME_SESSION_FAILURE = 'session/RESUME_SESSION_FAILURE';

export const SESSION_EXPIRED = 'session/SESSION_EXPIRED';

export const SESSION_REDIRECT_URL = 'session/REDIRECT_URL';

export const PING_SESSION_REQUESTED = 'session/PING_SESSION_REQUESTED';
export const PING_SESSION_SUCCESS = 'session/PING_SESSION_SUCCESS';
export const PING_SESSION_FAILURE = 'session/PING_SESSION_FAILURE';
export const PING_CLEAR = 'session/PING_CLEAR';

export const IMPERSONATE_ADMIN_REQUESTED =
  'account/IMPERSONATE_ADMIN_REQUESTED';
export const IMPERSONATE_ADMIN_SUCCESS = 'account/IMPERSONATE_ADMIN_SUCCESS';
export const IMPERSONATE_ADMIN_FAILURE = 'account/IMPERSONATE_ADMIN_FAILURE';

export const IMPERSONATE_FUNDRAISER_REQUESTED =
  'account/IMPERSONATE_FUNDRAISER_REQUESTED';
export const IMPERSONATE_FUNDRAISER_SUCCESS =
  'account/IMPERSONATE_FUNDRAISER_SUCCESS';
export const IMPERSONATE_FUNDRAISER_FAILURE =
  'account/IMPERSONATE_FUNDRAISER_FAILURE';

export const DELETE_IMPERSONATE_ADMIN_REQUESTED =
  'account/DELETE_IMPERSONATE_ADMIN_REQUESTED';
export const DELETE_IMPERSONATE_ADMIN_SUCCESS =
  'account/DELETE_IMPERSONATE_ADMIN_SUCCESS';
export const DELETE_IMPERSONATE_ADMIN_FAILURE =
  'account/DELETE_IMPERSONATE_ADMIN_FAILURE';

export const LOG_OUT_REQUESTED = 'session/LOG_OUT_REQUESTED';
export const LOG_OUT_SUCCESS = 'session/LOG_OUT_SUCCESS';
export const LOG_OUT_FAILURE = 'session/LOG_OUT_FAILURE';

interface SessionAccount {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  hasMfaActive: boolean;
  // The floor this account's next password has to meet, reported per account because system
  // accounts are held to a higher one. Absent on an API that predates it.
  passwordPolicy?: { minLength: number; maxLength: number };
}

// Admin role levels as returned by the API (lowercase)
export const AdminRoleLevel = {
  Owner: 'owner',
  Admin: 'admin',
  Viewer: 'viewer'
} as const;

// Admin context types as returned by the API (camelCase)
export const AdminContextTypeApi = {
  System: 'system',
  Organization: 'organization',
  Campaign: 'campaign',
  Event: 'event',
  EventOrganizer: 'eventOrganizer',
  CampaignOrganizer: 'campaignOrganizer'
} as const;

export interface Session {
  processing: boolean;
  pingProcessing: boolean;
  isSystemAdmin: boolean;
  isAdmin: boolean;
  isCampaignAdmin: boolean;
  isEventAdmin: boolean;
  isEventOrganizerAdmin: boolean;
  isFundraiser: boolean;
  isOrganizationAdmin: boolean;
  isCampaignOrganizerAdmin: boolean;
  adminRole: string | null;
  adminContextType: string | null;
  /**
   * The API will refuse everything outside the calls that complete an MFA enrolment. Computed
   * server-side; absent on an older API, where it reads as undefined and the legacy organisation
   * check is the only trigger, exactly as before.
   */
  requiresMfaEnrollment?: boolean;
  /**
   * A password rotation has been demanded of this account. The API refuses everything outside the
   * calls that satisfy it, and refuses MFA setup until it is satisfied, so this wall takes
   * precedence over the enrolment one.
   */
  requiresPasswordReset?: boolean;
  key?: any;
  account?: SessionAccount;
  organization?: any;
  campaigns?: any;
  campaignOrganizers?: any;
  expiry: any;
  impersonated: boolean;
  impersonator: any;
  refresh: any;
  error: any;
  errorList: any[];
  unauthorized: boolean;
  needGeneralRedirect: boolean;
  logOutRecord: any;
  redirectUrl?: string;
  permissions?: Permissions;
  // MFA two-phase flow state (never persisted to localStorage)
  pendingSessionToken: string | null;
  mfaPending: boolean;
  mfaTimestamp: number | null;
}

export enum PermissionValues {
  Allow = 'allow',
  Deny = 'deny'
}

export interface Permissions {
  exportData?: (typeof PermissionValues)[keyof typeof PermissionValues];
}

export const isOrgAdminRestricted = (session: Session): boolean => {
  return (
    get(session, 'isOrganizationAdmin') &&
    get(session, 'permissions.exportData') === PermissionValues.Deny
  );
};

// ──── Axis 1: Context type ("credential") ────
// Answers: "where does this user have access?"

export const isSystemCredential = (session: Session): boolean =>
  session.adminContextType === AdminContextTypeApi.System;

export const isOrganizationCredential = (session: Session): boolean =>
  session.adminContextType === AdminContextTypeApi.Organization;

export const isCampaignCredential = (session: Session): boolean =>
  session.adminContextType === AdminContextTypeApi.Campaign;

export const isEventCredential = (session: Session): boolean =>
  session.adminContextType === AdminContextTypeApi.Event;

export const isEventOrganizerCredential = (session: Session): boolean =>
  session.adminContextType === AdminContextTypeApi.EventOrganizer;

export const isCampaignOrganizerCredential = (session: Session): boolean =>
  session.adminContextType === AdminContextTypeApi.CampaignOrganizer;

// ──── Axis 2: Role level (universal, context-independent) ────
// Answers: "what can this user do?"

/** User has owner-level access (full control) */
export const isOwner = (session: Session): boolean =>
  session.adminRole === AdminRoleLevel.Owner;

/** User can perform write operations (owner or admin) */
export const isReadWrite = (session: Session): boolean =>
  session.adminRole === AdminRoleLevel.Owner || session.adminRole === AdminRoleLevel.Admin;

/** User has view-only access */
export const isReadOnly = (session: Session): boolean =>
  session.adminRole === AdminRoleLevel.Viewer;

// ──── Composite helpers ────

/** User is a system-level owner (full system control) */
export const isSystemOwner = (session: Session): boolean =>
  isSystemCredential(session) && isOwner(session);

/** User is a system-level admin who can perform write operations */
export const isSystemReadWrite = (session: Session): boolean =>
  isSystemCredential(session) && isReadWrite(session);

// ──── Membership helpers ────

/** Checks if an account's adminMemberships contain a system-owner membership */
export const hasSystemOwnerMembership = (adminMemberships: any[] = []): boolean =>
  adminMemberships.some(
    (m: any) => m.contextType === AdminContextTypeApi.System && m.role === AdminRoleLevel.Owner
  );

/**
 * Checks if an account's adminMemberships contain a system membership of any role. Owner, Admin
 * and Viewer all hold system-level credentials and carry the same rules.
 */
export const hasSystemMembership = (adminMemberships?: any[] | null): boolean =>
  (adminMemberships ?? []).some(
    (m: any) => m?.contextType === AdminContextTypeApi.System
  );

const initialState: Session = {
  processing: true,
  pingProcessing: false,
  isOrganizationAdmin: false,
  isSystemAdmin: false,
  isAdmin: false,
  isCampaignAdmin: false,
  isEventAdmin: false,
  isEventOrganizerAdmin: false,
  isCampaignOrganizerAdmin: false,
  isFundraiser: false,
  adminRole: null,
  adminContextType: null,
  requiresMfaEnrollment: false,
  requiresPasswordReset: false,
  key: undefined,
  account: undefined,
  organization: undefined,
  expiry: undefined,
  impersonated: false,
  impersonator: undefined,
  refresh: undefined,
  error: undefined,
  errorList: [],
  unauthorized: true,
  needGeneralRedirect: false,
  logOutRecord: Record.getDefaultState(),
  redirectUrl: undefined,
  pendingSessionToken: null,
  mfaPending: false,
  mfaTimestamp: null
};

export default (state = initialState, action: any) => {
  let newState = state;
  switch (action.type) {
    case RESUME_SESSION_REQUESTED:
    case AUTHENTICATE_REQUESTED:
    case VERIFY_MFA_REQUESTED:
      newState = {
        ...state,
        ...initialState,
        pendingSessionToken: state.pendingSessionToken,
        mfaPending: state.mfaPending,
        mfaTimestamp: state.mfaTimestamp,
        error: false,
        processing: true,
        pingProcessing: state.pingProcessing
      };
      break;

    case PING_SESSION_SUCCESS:
    case RESUME_SESSION_SUCCESS:
    case AUTHENTICATE_SUCCESS:
    case VERIFY_MFA_SUCCESS:
    case ACTIVATE_USER_SUCCESS:
    case IMPERSONATE_ADMIN_SUCCESS: {
      const isSystemAdmin = get(action.payload, 'data.isSystemAdmin');
      const isOrganizationAdmin = get(
        action.payload,
        'data.isOrganizationAdmin'
      );
      const isCampaignAdmin = get(action.payload, 'data.isCampaignAdmin');
      const isEventAdmin = get(action.payload, 'data.isEventAdmin');
      const isEventOrganizerAdmin = get(
        action.payload,
        'data.isEventOrganizerAdmin'
      );
      const isCampaignOrganizerAdmin = get(action.payload, 'data.isCampaignOrganizerAdmin');

      newState = {
        ...state,
        ...action.payload.data,
        processing: false,
        error: false,
        unauthorized: false,
        needGeneralRedirect: isCampaignAdmin || isEventAdmin,
        isCampaignAdmin: !isOrganizationAdmin && isCampaignAdmin,
        isAdmin:
          isSystemAdmin ||
          isOrganizationAdmin ||
          isCampaignAdmin ||
          isEventAdmin ||
          isEventOrganizerAdmin ||
          isCampaignOrganizerAdmin,
        pendingSessionToken: null,
        mfaPending: false,
        mfaTimestamp: null
      };

      if (action.payload.data && action.payload.data !== 'undefined') {
        localStorage.setItem('session', JSON.stringify(action.payload.data));
      } else {
        localStorage.removeItem('session');
      }

      break;
    }
    case RESUME_SESSION_NOT_POSSIBLE:
    case RESUME_SESSION_FAILURE:
    case AUTHENTICATE_FAILURE: {
      newState = {
        ...state,
        ...initialState,
        processing: false,
        error: true,
        errorList: get(action.payload, 'error'),
        key: null,
        pendingSessionToken: null,
        mfaPending: false,
        mfaTimestamp: null
      };
      break;
    }

    case MFA_REQUIRED:
      newState = {
        ...initialState,
        processing: false,
        unauthorized: true,
        pendingSessionToken: action.payload.pendingSessionToken,
        mfaPending: true,
        mfaTimestamp: action.payload.mfaTimestamp
      };
      break;

    case VERIFY_MFA_FAILURE:
      newState = {
        ...state,
        processing: false,
        error: true,
        errorList: get(action.payload, 'error'),
        // Keep MFA state intact so user can retry
        pendingSessionToken: state.pendingSessionToken,
        mfaPending: state.mfaPending,
        mfaTimestamp: state.mfaTimestamp
      };
      break;

    case MFA_CLEAR:
      newState = {
        ...state,
        pendingSessionToken: null,
        mfaPending: false,
        mfaTimestamp: null,
        error: false,
        errorList: []
      };
      break;

    case PING_SESSION_FAILURE:
      newState = {
        ...state,
        pingProcessing: true,
        key: null
      };
      break;

    case LOG_OUT_SUCCESS:
    case LOG_OUT_FAILURE:
      newState = {
        ...state,
        ...initialState,
        processing: false,
        key: null,
        pendingSessionToken: null,
        mfaPending: false,
        mfaTimestamp: null
      };
      break;

    case SESSION_EXPIRED:
      return {
        ...state,
        unauthorized: true
      };

    case SESSION_REDIRECT_URL:
      return {
        ...state,
        redirectUrl: action.payload.data.redirectUrl
      };

    case DELETE_IMPERSONATE_ADMIN_SUCCESS: {
      const restoredSession = action.payload.data;
      newState = {
        ...state,
        ...restoredSession,
        processing: false,
        needGeneralRedirect: false
      };
      if (restoredSession) {
        localStorage.setItem('session', JSON.stringify(restoredSession));
      }
      break;
    }

    case LOG_OUT_REQUESTED:
      localStorage.removeItem('session');
      localStorage.removeItem(localStorageKeys.PAYMENT_INFO);
      newState = {
        ...state,
        ...initialState,
        processing: false,
        key: null,
        pendingSessionToken: null,
        mfaPending: false,
        mfaTimestamp: null,
        logOutRecord: entityHandlers.getRecordRequestedHandler(state, action).logOutRecord
      };
      break;

    case GET_ORGANIZATION_SUCCESS:
      if (state.isOrganizationAdmin) {
        const paymentPlatforms = get(
          action,
          'payload.data.data.paymentPlatforms'
        );
        const orgId = get(action, 'payload.data.data.id');
        const isConnected = paymentPlatforms && paymentPlatforms.length !== 0;
        // giftAidStatus is always written, at minimum `unknown`: the general
        // layout treats a cache without it as stale and fetches the record again.
        writeOrgInfo({
          isConnected,
          orgId,
          giftAidStatus: readGiftAidStatus(
            get(action, `payload.data.data.${giftAidStatusPath}`)
          )
        });
      }

      break;

    case PING_CLEAR:
      newState = {
        ...state,
        pingProcessing: false
      };
      break;

    default:
      return state;
  }

  return newState;
};

export const setRedirectUrl = (redirectUrl: string) => ({
  type: SESSION_REDIRECT_URL,
  payload: { data: { redirectUrl } }
});

export const mockAuthenticate = (_email: string, _password: string) => ({
  type: AUTHENTICATE_SUCCESS,
  payload: { data: { key: 'mockKey' } }
});

export const authenticate = (
  email: string,
  password: string,
  remember = false,
  verificationToken: string | null = null
) => {
  return async (dispatch: any) => {
    dispatch({ type: AUTHENTICATE_REQUESTED });

    try {
      const res = await fetch(`${constants.baseApiHost}/api/v3/session/login`, {
        method: 'POST',
        headers: {
          ApiKey: constants.apikey,
          'Content-Type': 'application/json',
          ...(verificationToken ? { Verification: verificationToken } : {})
        },
        body: JSON.stringify({ email, password, remember })
      });

      const json = await res.json();

      if (!res.ok || !json.success) {
        dispatch({
          type: AUTHENTICATE_FAILURE,
          payload: { status: res.status, ...json }
        });
        return;
      }

      const { data } = json;

      if (data.status === 'complete') {
        dispatch({
          type: AUTHENTICATE_SUCCESS,
          payload: { data: data.session }
        });
      } else if (data.status === 'mfaRequired') {
        dispatch({
          type: MFA_REQUIRED,
          payload: {
            pendingSessionToken: data.pendingSessionToken,
            mfaTimestamp: Date.now()
          }
        });
      } else {
        dispatch({
          type: AUTHENTICATE_FAILURE,
          payload: { status: res.status, error: [{ error: 'unexpected.response', param: 'status' }] }
        });
      }
    } catch {
      dispatch({
        type: AUTHENTICATE_FAILURE,
        payload: { status: 0, error: [{ error: 'network.error', param: 'network' }] }
      });
    }
  };
};

export const resumeSession = (verificationToken = null) => {
  return (dispatch: any, getState: any) => {
    const localStorageSession = localStorage['session'];
    const session =
      localStorageSession && localStorageSession !== 'undefined'
        ? JSON.parse(localStorageSession)
        : null;

    if (session && session.key) {
      const state = getState();
      dispatch({
        [RSAA]: {
          endpoint: `${constants.baseApiHost}/api/v2/session`,
          method: 'GET',
          headers: {
            ApiKey: constants.apikey,
            Authorization: `bearer ${session.key}`,
            'Content-Type': 'application/json',
            Verification: verificationToken
          },
          types: [
            RESUME_SESSION_REQUESTED,
            RESUME_SESSION_SUCCESS,
            RESUME_SESSION_FAILURE
          ]
        }
      });
    } else {
      dispatch({ type: RESUME_SESSION_NOT_POSSIBLE });
    }
  };
};

export const impersonateAdmin = (id: any, verificationToken = null) => {
  return entityHandlers.createRecord(
    'session/impersonate',
    IMPERSONATE_ADMIN_REQUESTED,
    IMPERSONATE_ADMIN_SUCCESS,
    IMPERSONATE_ADMIN_FAILURE,
    { accountId: id },
    null,
    'impersonatedRecord',
    'v2/',
    verificationToken
  );
};

export const impersonateFundraiser = (id: any, verificationToken = null) => {
  return entityHandlers.createRecord(
    'session/impersonate/inactive',
    IMPERSONATE_FUNDRAISER_REQUESTED,
    IMPERSONATE_FUNDRAISER_SUCCESS,
    IMPERSONATE_FUNDRAISER_FAILURE,
    { accountId: id },
    null,
    'impersonatedFundraiserRecord',
    'v2/',
    verificationToken
  );
};

export const logOut = (verificationToken: string | null = null) => {
  return entityHandlers.createRecord(
    'session/logout',
    LOG_OUT_REQUESTED,
    LOG_OUT_SUCCESS,
    LOG_OUT_FAILURE,
    null,
    null,
    'logOutRecord',
    'v2/',
    verificationToken
  );
};

export const deleteImpersonation = (verificationToken: string | null = null) => {
  const endpoint = `${constants.baseApiHost}/api/v2/session/impersonate`;
  const recordKey = 'impersonatedRecord';
  return (dispatch: any, getState: any) => {
    const state = getState();
    dispatch({
      [RSAA]: {
        endpoint: endpoint,
        method: 'DELETE',
        headers: {
          ApiKey: constants.apikey,
          Authorization: `bearer ${state.session.key}`,
          'Content-Type': 'application/json',
          Verification: verificationToken
        },
        types: [
          {
            type: DELETE_IMPERSONATE_ADMIN_REQUESTED,
            payload: (_action: any, _state: any) => ({ key: recordKey })
          },
          {
            type: DELETE_IMPERSONATE_ADMIN_SUCCESS,
            payload: async (_action: any, _state: any, res: any) => {
              const json = await res.json();
              return {
                key: recordKey,
                data: json.data
              };
            }
          },
          {
            type: DELETE_IMPERSONATE_ADMIN_FAILURE,
            payload: async (_action: any, _state: any, res: any) => {
              const json = await res.json();
              return {
                key: recordKey,
                status: res.status,
                ...json
              };
            }
          }
        ]
      }
    });
  };
};

export const refreshSession = (
  accountId: any,
  code: any,
  verificationToken: string | null = null
) => {
  return async (dispatch: any) => {
    dispatch({ type: RESUME_SESSION_REQUESTED });

    try {
      const res = await fetch(`${constants.baseApiHost}/api/v3/session/refresh`, {
        method: 'POST',
        headers: {
          ApiKey: constants.apikey,
          'Content-Type': 'application/json',
          ...(verificationToken ? { Verification: verificationToken } : {})
        },
        body: JSON.stringify({ accountId, code })
      });

      const json = await res.json();

      if (!res.ok || !json.success) {
        // Refresh token is dead — drop the stored session so SecureRoute
        // doesn't keep retrying on every re-render.
        localStorage.removeItem('session');
        dispatch({
          type: RESUME_SESSION_FAILURE,
          payload: { status: res.status, ...json }
        });
        return;
      }

      const { data } = json;

      if (data.status === 'complete') {
        dispatch({
          type: RESUME_SESSION_SUCCESS,
          payload: { data: data.session }
        });
      } else if (data.status === 'mfaRequired') {
        // Old session is dead — clear localStorage immediately
        localStorage.removeItem('session');
        dispatch({
          type: MFA_REQUIRED,
          payload: {
            pendingSessionToken: data.pendingSessionToken,
            mfaTimestamp: Date.now()
          }
        });
      } else {
        localStorage.removeItem('session');
        dispatch({
          type: RESUME_SESSION_FAILURE,
          payload: { status: res.status, error: [{ error: 'unexpected.response', param: 'status' }] }
        });
      }
    } catch {
      localStorage.removeItem('session');
      dispatch({
        type: RESUME_SESSION_FAILURE,
        payload: { status: 0, error: [{ error: 'network.error', param: 'network' }] }
      });
    }
  };
};

export const pingSession = (verificationToken: string | null = null) => {
  return (dispatch: any, getState: any) => {
    const state = getState();
    if (state.session.key) {
      dispatch({
        [RSAA]: {
          endpoint: `${constants.baseApiHost}/api/v2/session/ping`,
          method: 'GET',
          headers: {
            ApiKey: constants.apikey,
            Authorization: `bearer ${state.session.key}`,
            'Content-Type': 'application/json',
            Verification: verificationToken
          },
          types: [
            PING_SESSION_REQUESTED,
            PING_SESSION_SUCCESS,
            PING_SESSION_FAILURE
          ]
        }
      });
    }
  };
};

export const pingClear = () => {
  return {
    type: PING_CLEAR
  };
};

export const verifyMfa = (
  pendingSessionToken: string,
  otp: string | null = null,
  otpRecoveryCode: string | null = null,
  verificationToken: string | null = null
) => {
  return async (dispatch: any) => {
    dispatch({ type: VERIFY_MFA_REQUESTED });

    try {
      const res = await fetch(`${constants.baseApiHost}/api/v3/session/login/verify-mfa`, {
        method: 'POST',
        headers: {
          ApiKey: constants.apikey,
          'Content-Type': 'application/json',
          ...(verificationToken ? { Verification: verificationToken } : {})
        },
        body: JSON.stringify({ pendingSessionToken, otp, otpRecoveryCode })
      });

      const json = await res.json();

      if (!res.ok || !json.success) {
        dispatch({
          type: VERIFY_MFA_FAILURE,
          payload: { status: res.status, ...json }
        });
        return;
      }

      dispatch({
        type: VERIFY_MFA_SUCCESS,
        payload: { data: json.data.session }
      });
    } catch {
      dispatch({
        type: VERIFY_MFA_FAILURE,
        payload: { status: 0, error: [{ error: 'network.error', param: 'network' }] }
      });
    }
  };
};

export const clearMfa = () => ({
  type: MFA_CLEAR
});
